/**
 * Strip non-DOM props that @mui/x-charts forwards into custom slot components
 * (bar / area / axisTickLabel / etc.) before they are spread onto a raw SVG
 * element like <rect>, <path>, or <text>.
 *
 * x-charts passes internal bookkeeping props (ownerState, dataIndex, xOrigin,
 * yOrigin, skipAnimation, ...) to slot components. When a custom slot spreads
 * `{...restProps}` straight onto an SVG host node, React 19 logs
 * "React does not recognize the `xOrigin` prop on a DOM element" (etc.) for
 * each one. These are not valid SVG attributes, so dropping them is safe and
 * removes the warning. Centralized here so every chart slot uses one strip
 * point and new charts don't reintroduce the leak.
 */
const NON_DOM_CHART_SLOT_PROPS = [
  'ownerState',
  'dataIndex',
  'xOrigin',
  'yOrigin',
  'skipAnimation',
] as const

export const stripChartSlotProps = <T extends object>(
  props: T,
): Omit<T, (typeof NON_DOM_CHART_SLOT_PROPS)[number]> => {
  const cleaned = { ...props } as Record<string, unknown>
  for (const key of NON_DOM_CHART_SLOT_PROPS) {
    delete cleaned[key]
  }
  return cleaned as Omit<T, (typeof NON_DOM_CHART_SLOT_PROPS)[number]>
}
